import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Button, Card, Col, DatePicker, Empty, Row, Select, Space, Table, Typography, message, Checkbox, Radio, Spin, Tag, Badge } from 'antd';
import { ArrowLeftOutlined, CheckOutlined, EnvironmentOutlined, EditOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate, useLocation } from 'react-router-dom';
import AreaMap from '../components/Map/AreaMap';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import '../style/Measurements.css';
import { formatMeasurementTimestamp, formatUtcTimestamp } from '../utils/measurementTime';

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

interface Area {
    id: number;
    name: string;
    areaType?: { id: number; name: string } | null;
    geom?: any;
}

interface Pattern {
    id: number;
    name: string;
    patternType?: string;
}

interface PatternPoint {
    id: number;
    pointIndex: number;
    label?: string | null;
    gpsLat?: number | null;
    gpsLon?: number | null;
    xLocal?: number;
    yLocal?: number;
}

interface Measurement {
    id: number;
    measurementCode: string;
    createdAt: string;
    measuredAt?: string | null;
    measuredTimezone?: string | null;
    measuredUtcOffsetMinutes?: number | null;
    timeSource?: string | null;
    timezoneSource?: string | null;
    areaId?: number | null;
    area?: { id: number; name: string } | null;
    patternId?: number | null;
    pattern?: { id: number; name: string } | null;
    patternPointId?: number | null;
    device?: { id: number; label: string } | null;
    geom?: { type: string; coordinates: number[] } | null;
}

const MeasurementAssignPatterns: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [areas, setAreas] = useState<Area[]>([]);
    const [selectedAreaId, setSelectedAreaId] = useState<number | null>(null);
    const [patterns, setPatterns] = useState<Pattern[]>([]);
    const [selectedPatternId, setSelectedPatternId] = useState<number | null>(null);
    const [patternPoints, setPatternPoints] = useState<PatternPoint[]>([]);
    const [selectedPatternPointId, setSelectedPatternPointId] = useState<number | null>(null);
    const [measurements, setMeasurements] = useState<Measurement[]>([]);
    const [selectedMeasurementIds, setSelectedMeasurementIds] = useState<number[]>([]);
    const [focusedMeasurement, setFocusedMeasurement] = useState<Measurement | null>(null);
    const appliedMeasurementParamRef = useRef<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [assigning, setAssigning] = useState(false);
    const [areaTypeTemplates, setAreaTypeTemplates] = useState<Map<number, string>>(new Map());
    const [checkedAreaTypes, setCheckedAreaTypes] = useState<Set<number>>(new Set());
    const [assignmentMode, setAssignmentMode] = useState<'point' | 'pattern'>('point');
    const [templateUrl, setTemplateUrl] = useState<string | null>(null);
    const [loadingTemplate, setLoadingTemplate] = useState(false);
    const [imageBounds, setImageBounds] = useState({ left: 0, top: 0, width: 0, height: 0 });
    const previewRef = useRef<HTMLDivElement>(null);
    const templateObjectUrl = useRef<string | null>(null);
    const imageRef = useRef<HTMLImageElement | null>(null);
    const getInitialDateRange = (): [Dayjs, Dayjs] => {
        try {
            const stored = localStorage.getItem('measurementDateRange');
            if (stored) {
                const { from, to } = JSON.parse(stored);
                return [dayjs(from), dayjs(to)];
            }
        } catch (e) {
            console.warn('Failed to load date range from localStorage', e);
        }
        return [dayjs().subtract(7, 'day'), dayjs()];
    };
    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(getInitialDateRange());

    const fetchAreas = useCallback(async () => {
        try {
            const res = await axios.get(ENDPOINTS.AREAS_V2, { withCredentials: true });
            const allAreas = res.data?.data || res.data || [];
            
            // Fetch full details for each area to ensure we have geometry
            const areasWithGeometry = await Promise.all(
                allAreas.map(async (area: Area) => {
                    try {
                        const detailRes = await axios.get(ENDPOINTS.AREA_DETAIL(area.id), { 
                            withCredentials: true 
                        });
                        return detailRes.data?.data || detailRes.data || area;
                    } catch (err) {
                        console.warn(`Failed to load details for area ${area.id}`, err);
                        return area;
                    }
                })
            );
            
            setAreas(areasWithGeometry);
        } catch (error) {
            console.error('Failed to load areas', error);
        }
    }, []);

    const fetchMeasurements = useCallback(async (areaId: number) => {
        setLoading(true);
        try {
            const params: Record<string, any> = { limit: 1000 };
            if (dateRange && dateRange[0] && dateRange[1]) {
                params.from_date = dateRange[0].startOf('day').toISOString();
                params.to_date = dateRange[1].endOf('day').toISOString();
            }

            const res = await axios.get(ENDPOINTS.MEASUREMENTS_V2, {
                params,
                withCredentials: true,
            });
            
            const allMeasurements = res.data?.data || res.data || [];
            // Filter: all measurements in this area (including those with patterns for editing)
            const filtered = allMeasurements.filter(
                (m: Measurement) => m.areaId === areaId
            );
            setMeasurements(filtered);
        } catch (error) {
            console.error('Failed to load measurements', error);
            message.error('Failed to load measurements');
        } finally {
            setLoading(false);
        }
    }, [dateRange]);

    const fetchPatterns = useCallback(async (areaId: number) => {
        try {
            const res = await axios.get(ENDPOINTS.AREA_PATTERNS(areaId), {
                params: { limit: 200 },
                withCredentials: true,
            });
            setPatterns(res.data?.data || res.data || []);
        } catch (error) {
            console.error('Failed to load patterns', error);
            setPatterns([]);
        }
    }, []);

    const fetchPatternPoints = useCallback(async (patternId: number) => {
        try {
            const res = await axios.get(ENDPOINTS.PATTERN_POINTS(patternId), {
                withCredentials: true,
            });
            const points = res.data?.data || res.data || [];
            console.log('Pattern points loaded:', points);
            console.log('Points with local coords:', points.filter((p: PatternPoint) => p.xLocal !== null && p.xLocal !== undefined));
            setPatternPoints(points);
        } catch (error) {
            console.error('Failed to load pattern points', error);
            setPatternPoints([]);
        }
    }, []);

    // Pre-select area from URL measurement parameter FIRST
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const measurementIdParam = searchParams.get('measurementId');
        
        if (measurementIdParam && areas.length > 0 && !selectedAreaId) {
            const measurementId = parseInt(measurementIdParam, 10);
            
            // Fetch the specific measurement to get its areaId
            (async () => {
                try {
                    const res = await axios.get(ENDPOINTS.MEASUREMENT_DETAIL(measurementId), {
                        withCredentials: true,
                    });
                    const measurement = res.data?.data || res.data;
                    
                    if (measurement?.areaId) {
                        // Pre-select the area
                        setSelectedAreaId(measurement.areaId);
                    }
                } catch (error) {
                    console.error('Failed to load measurement for pre-selection', error);
                }
            })();
        }
    }, [location.search, areas, selectedAreaId]);

    useEffect(() => {
        fetchAreas();
    }, [fetchAreas]);

    useEffect(() => {
        if (selectedAreaId) {
            fetchMeasurements(selectedAreaId);
            fetchPatterns(selectedAreaId);
        } else {
            setMeasurements([]);
            setPatterns([]);
            setPatternPoints([]);
            setSelectedPatternId(null);
        }
        setSelectedMeasurementIds([]);
    }, [selectedAreaId, fetchMeasurements, fetchPatterns]);

    // Auto-select first pattern when patterns load
    useEffect(() => {
        if (patterns.length > 0 && !selectedPatternId) {
            setSelectedPatternId(patterns[0].id);
        }
    }, [patterns, selectedPatternId]);

    // Pre-select and focus the measurement after it's loaded
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const measurementIdParam = searchParams.get('measurementId');

        if (
            !measurementIdParam ||
            measurements.length === 0 ||
            selectedMeasurementIds.length > 0 ||
            appliedMeasurementParamRef.current === measurementIdParam
        ) {
            return;
        }

        const measurementId = parseInt(measurementIdParam, 10);
        const measurement = measurements.find(m => m.id === measurementId);

        if (measurement) {
            appliedMeasurementParamRef.current = measurementIdParam;
            setSelectedMeasurementIds([measurementId]);
            setFocusedMeasurement(measurement);
        }
    }, [location.search, measurements, selectedMeasurementIds]);

    useEffect(() => {
        if (selectedPatternId) {
            fetchPatternPoints(selectedPatternId);
            setSelectedPatternPointId(null);
        } else {
            setPatternPoints([]);
            setSelectedPatternPointId(null);
        }
    }, [selectedPatternId, fetchPatternPoints]);

    useEffect(() => {
        if (assignmentMode !== 'point' && selectedPatternPointId !== null) {
            setSelectedPatternPointId(null);
        }
    }, [assignmentMode, selectedPatternPointId]);

    // Cleanup stale measurement IDs from selection when measurements list changes
    useEffect(() => {
        if (selectedMeasurementIds.length > 0) {
            const validIds = measurements.map(m => m.id);
            const filteredIds = selectedMeasurementIds.filter(id => validIds.includes(id));
            
            // Only update if there are actually stale IDs to remove
            if (filteredIds.length !== selectedMeasurementIds.length) {
                console.log('Removing stale measurement IDs from selection:', 
                    selectedMeasurementIds.filter(id => !validIds.includes(id)));
                setSelectedMeasurementIds(filteredIds);
                
                // Also clear focused measurement if it's no longer in the list
                if (focusedMeasurement && !validIds.includes(focusedMeasurement.id)) {
                    console.log('Clearing stale focused measurement:', focusedMeasurement.id);
                    setFocusedMeasurement(null);
                }
            }
        }
    }, [measurements]);

    const selectedArea = useMemo(
        () => areas.find(a => a.id === selectedAreaId) || null,
        [areas, selectedAreaId]
    );

    // Load area type template (for map overlay)
    useEffect(() => {
        const loadTemplate = async () => {
            if (!selectedArea?.areaType?.id) return;
            const typeId = selectedArea.areaType.id;
            if (checkedAreaTypes.has(typeId)) return;
            
            setCheckedAreaTypes(prev => new Set(prev).add(typeId));
            
            try {
                const res = await axios.get(`${ENDPOINTS.AREA_TYPES_V2}/${typeId}`, {
                    withCredentials: true,
                });
                const hasTemplate = res.data?.data?.templateImage || res.data?.templateImage;
                
                if (hasTemplate) {
                    const imageUrl = `${ENDPOINTS.AREA_TYPES_V2}/${typeId}/template-image`;
                    setAreaTypeTemplates(prev => {
                        const next = new Map(prev);
                        next.set(typeId, imageUrl);
                        return next;
                    });
                }
            } catch (err) {
                console.warn('Failed to load area type template', err);
            }
        };
        loadTemplate();
    }, [selectedArea, checkedAreaTypes]);

    // Load template image as blob for pattern preview
    useEffect(() => {
        const loadTemplateBlob = async () => {
            if (!selectedArea?.areaType?.id) {
                setTemplateUrl(null);
                return;
            }
            setLoadingTemplate(true);
            try {
                const res = await axios.get(`${ENDPOINTS.AREA_TYPES_V2}/${selectedArea.areaType.id}/template-image/download?t=${Date.now()}`, {
                    withCredentials: true,
                    responseType: 'blob',
                });
                if (templateObjectUrl.current) URL.revokeObjectURL(templateObjectUrl.current);
                const url = URL.createObjectURL(res.data as Blob);
                templateObjectUrl.current = url;
                setTemplateUrl(url);
            } catch (err) {
                console.warn('Failed to load template blob', err);
                if (templateObjectUrl.current) URL.revokeObjectURL(templateObjectUrl.current);
                templateObjectUrl.current = null;
                setTemplateUrl(null);
            } finally {
                setLoadingTemplate(false);
            }
        };
        loadTemplateBlob();
    }, [selectedArea]);

    // Calculate image bounds for pattern preview
    useEffect(() => {
        if (!templateUrl || !previewRef.current || !selectedPatternId || patternPoints.length === 0) return;

        const img = new Image();
        imageRef.current = img;
        
        const calculateBounds = () => {
            const container = previewRef.current;
            const image = imageRef.current;
            if (!container || !image || !image.complete) return;

            const containerWidth = container.clientWidth;
            const containerHeight = container.clientHeight;
            const imageAspect = image.naturalWidth / image.naturalHeight;
            const containerAspect = containerWidth / containerHeight;

            let displayWidth, displayHeight, offsetX, offsetY;

            if (containerAspect > imageAspect) {
                displayHeight = containerHeight;
                displayWidth = displayHeight * imageAspect;
                offsetX = (containerWidth - displayWidth) / 2;
                offsetY = 0;
            } else {
                displayWidth = containerWidth;
                displayHeight = displayWidth / imageAspect;
                offsetX = 0;
                offsetY = (containerHeight - displayHeight) / 2;
            }

            console.log('Image bounds calculated:', { offsetX, offsetY, displayWidth, displayHeight });
            setImageBounds({
                left: offsetX,
                top: offsetY,
                width: displayWidth,
                height: displayHeight,
            });
        };

        img.onload = () => {
            setTimeout(calculateBounds, 150);
        };
        img.src = templateUrl;

        // Add resize observer to recalculate bounds on container resize
        const resizeObserver = new ResizeObserver(() => {
            calculateBounds();
        });

        if (previewRef.current) {
            resizeObserver.observe(previewRef.current);
        }

        return () => {
            resizeObserver.disconnect();
            imageRef.current = null;
        };
    }, [templateUrl, selectedPatternId, patternPoints]);

    // Cleanup template URL on unmount
    useEffect(() => {
        return () => {
            if (templateObjectUrl.current) URL.revokeObjectURL(templateObjectUrl.current);
        };
    }, []);

    const measurementMarkers = useMemo(() => {
        return measurements
            .filter(m => m.geom?.type === 'Point' && Array.isArray(m.geom.coordinates))
            .map(m => ({
                id: m.id,
                lat: m.geom!.coordinates[1],
                lng: m.geom!.coordinates[0],
                label: m.measurementCode,
                selected: selectedMeasurementIds.includes(m.id),
            }));
    }, [measurements, selectedMeasurementIds]);

    const patternPointMarkers = useMemo(() => {
        return patternPoints
            .filter(p => p.gpsLat != null && p.gpsLon != null)
            .map(p => ({
                id: p.id,
                lat: p.gpsLat!,
                lng: p.gpsLon!,
                label: p.label || `#${p.pointIndex}`,
                selected: p.id === selectedPatternPointId,
            }));
    }, [patternPoints, selectedPatternPointId]);

    const handlePatternPointClick = useCallback((pointId: string | number) => {
        const numericId = typeof pointId === 'string' ? parseInt(pointId, 10) : pointId;
        setSelectedPatternPointId((prev) => (prev === numericId ? null : numericId));
    }, []);

    const handleMeasurementClick = useCallback((measurementId: string | number) => {
        const numericId = typeof measurementId === 'string' ? parseInt(measurementId, 10) : measurementId;
        const isSelected = selectedMeasurementIds.includes(numericId);
        setSelectedMeasurementIds((prev) => (
            isSelected ? prev.filter((id) => id !== numericId) : [...prev, numericId]
        ));
        if (isSelected) {
            if (focusedMeasurement?.id === numericId) {
                setFocusedMeasurement(null);
            }
            return;
        }
        const measurement = measurements.find((m) => m.id === numericId) || null;
        setFocusedMeasurement(measurement);
    }, [measurements, selectedMeasurementIds, focusedMeasurement]);

    const mapInitialView = useMemo(() => {
        if (focusedMeasurement?.geom?.type === 'Point' && Array.isArray(focusedMeasurement.geom.coordinates)) {
            return {
                longitude: focusedMeasurement.geom.coordinates[0],
                latitude: focusedMeasurement.geom.coordinates[1],
                zoom: 17,
            };
        }
        return undefined;
    }, [focusedMeasurement]);

    const handleAssign = async () => {
        if (selectedMeasurementIds.length === 0) {
            message.warning('Please select measurements to assign');
            return;
        }

        if (!selectedPatternId) {
            message.warning('Please select a pattern');
            return;
        }

        if (assignmentMode === 'point' && !selectedPatternPointId) {
            message.warning('Please select a pattern point or switch to pattern-only mode');
            return;
        }

        setAssigning(true);
        try {
            const res = await axios.patch(
                ENDPOINTS.MEASUREMENTS_BULK_UPDATE_PATTERN,
                {
                    measurementIds: selectedMeasurementIds,
                    patternId: selectedPatternId,
                    patternPointId: assignmentMode === 'point' ? selectedPatternPointId : null,
                },
                { withCredentials: true }
            );

            message.success(res.data.message || `${res.data.data.updatedCount} measurements assigned successfully`);
            
            // Update local state to avoid full reload
            const assignedPattern = patterns.find((p) => p.id === selectedPatternId);
            const assignedIds = new Set(selectedMeasurementIds);
            setMeasurements((prev) =>
                prev.map((m) =>
                    assignedIds.has(m.id)
                        ? {
                              ...m,
                              patternId: selectedPatternId,
                              pattern: assignedPattern
                                  ? { id: assignedPattern.id, name: assignedPattern.name }
                                  : m.pattern,
                              patternPointId: assignmentMode === 'point' ? selectedPatternPointId : null,
                          }
                        : m
                )
            );
            setSelectedMeasurementIds([]);
            if (focusedMeasurement && assignedIds.has(focusedMeasurement.id)) {
                setFocusedMeasurement(null);
            }
        } catch (error: any) {
            console.error('Failed to assign measurements', error);
            message.error(error.response?.data?.message || 'Failed to assign measurements');
        } finally {
            setAssigning(false);
        }
    };

    const toggleSelectAll = (checked: boolean) => {
        if (checked) {
            setSelectedMeasurementIds(measurements.map(m => m.id));
        } else {
            setSelectedMeasurementIds([]);
            setFocusedMeasurement(null);
        }
    };

    const columns: ColumnsType<Measurement> = [
        {
            title: (
                <Checkbox
                    checked={selectedMeasurementIds.length === measurements.length && measurements.length > 0}
                    indeterminate={selectedMeasurementIds.length > 0 && selectedMeasurementIds.length < measurements.length}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                />
            ),
            key: 'select',
            width: 50,
            render: (_, record) => (
                <Checkbox
                    checked={selectedMeasurementIds.includes(record.id)}
                    onChange={(e) => {
                        if (e.target.checked) {
                            setSelectedMeasurementIds([...selectedMeasurementIds, record.id]);
                        } else {
                            setSelectedMeasurementIds(selectedMeasurementIds.filter(id => id !== record.id));
                            if (focusedMeasurement?.id === record.id) {
                                setFocusedMeasurement(null);
                            }
                        }
                    }}
                />
            ),
        },
        {
            title: 'Code',
            dataIndex: 'measurementCode',
            key: 'measurementCode',
        },
        {
            title: 'Current Pattern',
            key: 'currentPattern',
            render: (_, record) => {
                if (!record.patternId) {
                    return <Badge status="default" text="Not assigned" />;
                }
                return (
                    <Space direction="vertical" size={0}>
                        <Tag color="blue" icon={<EditOutlined />}>
                            {record.pattern?.name || `Pattern ${record.patternId}`}
                        </Tag>
                        {record.patternPointId && (
                            <Tag color="green" style={{ marginTop: 2 }}>
                                Point assigned
                            </Tag>
                        )}
                    </Space>
                );
            },
        },
        {
            title: 'Date',
            key: 'measuredAt',
            render: (_: unknown, record: Measurement) => record.measuredAt
                ? formatMeasurementTimestamp(record.measuredAt, record)
                : formatUtcTimestamp(record.createdAt),
            width: 150,
        },
        {
            title: 'Device',
            dataIndex: ['device', 'label'],
            key: 'device',
            render: (_, record) => record.device?.label || '—',
        },
        {
            title: 'GPS',
            key: 'gps',
            width: 60,
            render: (_, record) => (
                record.geom?.type === 'Point' ? (
                    <CheckOutlined style={{ color: '#52c41a' }} />
                ) : (
                    <Text type="secondary">—</Text>
                )
            ),
        },
    ];

    return (
        <div className="measurements-page measurement-assign-patterns-page">
            <Row justify="start" align="middle" className="measurements-header">
                <Col>
                    <Space size="middle" align="center">
                        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/measurements')}>
                            Back
                        </Button>
                        <Title level={3} style={{ margin: 0 }}>Assign measurements to patterns</Title>
                    </Space>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col xs={24} xl={14}>
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <Card size="small" className="assign-patterns-date-filter-card" style={{ background: '#f5f5f5' }}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Date range filter</Text>
                                <RangePicker
                                    className="assign-patterns-date-range"
                                    value={dateRange}
                                    onChange={(range) => {
                                        if (range) {
                                            const newRange = range as [Dayjs, Dayjs];
                                            setDateRange(newRange);
                                            localStorage.setItem('measurementDateRange', JSON.stringify({
                                                from: newRange[0].format('YYYY-MM-DD'),
                                                to: newRange[1].format('YYYY-MM-DD'),
                                            }));
                                        }
                                    }}
                                    allowClear={false}
                                    style={{ width: '100%' }}
                                    format="DD/MM/YYYY"
                                />
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    Filters measurements by measured date
                                </Text>
                            </Space>
                        </Card>

                        <Card size="small" className="assign-patterns-workflow-card" style={{ background: '#e6f7ff' }}>
                            <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                <div>
                                    <Text strong>1. Select area</Text>
                                    <Select
                                        className="assign-patterns-area-select"
                                        showSearch
                                        placeholder="Choose area"
                                        value={selectedAreaId}
                                        onChange={setSelectedAreaId}
                                        style={{ width: '100%', marginTop: 4 }}
                                        optionFilterProp="children"
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        options={areas.map(a => ({
                                            value: a.id,
                                            label: `${a.name} ${a.areaType?.name ? `(${a.areaType.name})` : ''}`,
                                        }))}
                                    />
                                </div>

                                <div>
                                    <Text strong>2. Select pattern</Text>
                                    <Select
                                        className="assign-patterns-pattern-select"
                                        showSearch
                                        placeholder="Choose pattern"
                                        value={selectedPatternId}
                                        onChange={setSelectedPatternId}
                                        style={{ width: '100%', marginTop: 4 }}
                                        disabled={!selectedAreaId}
                                        options={patterns.map(p => ({
                                            value: p.id,
                                            label: `${p.name} ${p.patternType ? `(${p.patternType})` : ''}`,
                                        }))}
                                    />
                                </div>

                                <div>
                                    <Text strong>3. Assignment mode</Text>
                                    <Radio.Group 
                                        className="assign-patterns-mode"
                                        value={assignmentMode} 
                                        onChange={(e) => setAssignmentMode(e.target.value)}
                                        style={{ marginTop: 4, width: '100%' }}
                                        disabled={!selectedPatternId}
                                    >
                                        <Space direction="vertical" style={{ width: '100%' }}>
                                            <Radio value="point">
                                                Assign to specific pattern point
                                            </Radio>
                                            <Radio value="pattern">
                                                Assign to pattern only (no specific point)
                                            </Radio>
                                        </Space>
                                    </Radio.Group>
                                </div>

                                {assignmentMode === 'point' && (
                                    <div>
                                        <Text strong>4. Select pattern point</Text>
                                        <Select
                                            className="assign-patterns-point-select"
                                            showSearch
                                            placeholder="Choose pattern point"
                                            value={selectedPatternPointId}
                                            allowClear
                                            onChange={(value) =>
                                                setSelectedPatternPointId(typeof value === 'number' ? value : null)
                                            }
                                            style={{ width: '100%', marginTop: 4 }}
                                            disabled={!selectedPatternId || patternPoints.length === 0}
                                            options={patternPoints.map(p => ({
                                                value: p.id,
                                                label: p.label || `Point #${p.pointIndex}`,
                                            }))}
                                        />
                                        {patternPoints.length === 0 && selectedPatternId && (
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                This pattern has no defined points
                                            </Text>
                                        )}
                                    </div>
                                )}
                            </Space>
                        </Card>

                        <Card 
                            className="assign-patterns-table-card"
                            title={
                                <Space>
                                    <span>Area Measurements</span>
                                    {measurements.length > 0 && (
                                        <Space size="small">
                                            <Badge status="default" text={measurements.filter(m => !m.patternId).length + " unassigned"} />
                                            <Badge status="processing" text={measurements.filter(m => m.patternId).length + " assigned"} />
                                        </Space>
                                    )}
                                </Space>
                            }
                        >
                            {!selectedAreaId ? (
                                <Empty description="Select an area to see measurements" />
                            ) : (
                                <Table
                                    size="small"
                                    rowKey="id"
                                    columns={columns}
                                    dataSource={measurements}
                                    pagination={{
                                        pageSize: 15,
                                        showTotal: (total) => `${total} measurements`,
                                    }}
                                    loading={loading}
                                    locale={{
                                        emptyText: <Empty description="No measurements in this area" />,
                                    }}
                                    rowClassName={(record) =>
                                        selectedMeasurementIds.includes(record.id) ? 'area-row-selected' : ''
                                    }
                                    onRow={(record) => ({
                                        onClick: () => setFocusedMeasurement(record),
                                        style: { cursor: 'pointer' }
                                    })}
                                />
                            )}
                        </Card>
                    </Space>
                </Col>

                <Col xs={24} xl={10}>
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        {selectedPatternId && templateUrl && patternPoints.length > 0 && (
                            <Card title="Pattern preview" className="assign-patterns-preview-card">
                                <div
                                    ref={previewRef}
                                    style={{
                                        backgroundImage: `url(${templateUrl})`,
                                        backgroundSize: 'contain',
                                        backgroundRepeat: 'no-repeat',
                                        backgroundPosition: 'center',
                                        backgroundColor: '#fafafa',
                                        position: 'relative',
                                        width: '100%',
                                        height: '360px',
                                        border: '2px solid #d9d9d9',
                                        borderRadius: 8,
                                    }}
                                >
                                    {imageBounds.width > 0 && patternPoints.map((p) => (
                                        <div
                                            key={p.id}
                                            onClick={() => handlePatternPointClick(p.id)}
                                            style={{
                                                position: 'absolute',
                                                left: `${imageBounds.left + (p.xLocal || 0) * imageBounds.width}px`,
                                                top: `${imageBounds.top + (1 - (p.yLocal || 0)) * imageBounds.height}px`,
                                                transform: 'translate(-50%, -50%)',
                                                width: 24,
                                                height: 24,
                                                borderRadius: '50%',
                                                background: selectedPatternPointId === p.id ? '#52c41a' : '#fa8c16',
                                                border: '3px solid white',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: 11,
                                                fontWeight: 'bold',
                                                color: 'white',
                                                zIndex: 10,
                                                boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            {p.pointIndex}
                                        </div>
                                    ))}
                                </div>
                                {loadingTemplate && (
                                    <div style={{ textAlign: 'center', padding: 16 }}>
                                        <Spin />
                                    </div>
                                )}
                            </Card>
                        )}

                        <Card title="Area map" className="assign-patterns-map-card">
                            <div style={{ 
                                height: (!templateUrl || patternPoints.length === 0) ? '500px' : '350px',
                                backgroundColor: '#fafafa',
                                border: '2px solid #d9d9d9',
                                borderRadius: 8,
                                overflow: 'hidden'
                            }}>
                                <AreaMap
                                    mapKey={`assign-pattern-${selectedAreaId || 'none'}-${selectedPatternId || 'none'}`}
                                    areaGeometry={selectedArea?.geom}
                                    areaName={selectedArea?.name}
                                    measurements={measurementMarkers}
                                    points={patternPointMarkers}
                                    allAreas={selectedArea ? [selectedArea as any] : []}
                                    areaType={selectedArea?.areaType || null}
                                    areaTypeTemplates={areaTypeTemplates}
                                    onPointClick={handlePatternPointClick}
                                    onMeasurementClick={handleMeasurementClick}
                                />
                            </div>
                            <div className="assign-patterns-map-actions">
                                <Button 
                                    className="assign-patterns-assign-button"
                                    type="primary" 
                                    onClick={handleAssign}
                                    loading={assigning}
                                    disabled={
                                        selectedMeasurementIds.length === 0 || 
                                        !selectedPatternId ||
                                        (assignmentMode === 'point' && !selectedPatternPointId)
                                    }
                                    block
                                >
                                    Assign to pattern
                                </Button>
                            </div>
                        </Card>
                    </Space>
                </Col>
            </Row>
        </div>
    );
};

export default MeasurementAssignPatterns;
